import React from "react";
import { Paper, Box, Typography, Button } from "@mui/material";
import IconComponent from "../../Generals/Icons/IconComponent";

const FONT_FAMILY = '"Plus Jakarta Sans", sans-serif';

// Function aux for format long dates to short
const formatDateShort = (dateString) => {
  if (!dateString) return "";

  // if format "YYYY.MM.DD HH:MM:SS:MSS", extract only YYYY-MM-DD
  if (dateString.includes(".")) {
    const parts = dateString.split(" ");
    const datePart = parts[0]; // "2025.01.24"
    return datePart.replace(/\./g, "-"); // "2025-01-24"
  }

  // if other format, parse like date
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }); // "01/24/2025"
  } catch {
    return dateString;
  }
};

const FollowersPanelSidebar = ({
  open,
  onClose,
  signalLogin,
  followersData = [],
}) => {
  if (!open) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        width: 330,
        minWidth: 330,
        maxHeight: 600,
        borderRadius: "18px",
        p: 2,
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 32px rgba(44,55,122,0.18)",
        ml: 3,
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          {`Followers of Signal ${signalLogin ?? ""}`}
        </Typography>
        <Button onClick={onClose} sx={{ minWidth: "auto", p: 0 }}>
          <IconComponent icon="close" size="18px" />
        </Button>
      </Box>

      {/* Cards */}
      {followersData.length > 0 ? (
        followersData.map((follower, idx) => (
          <Paper
            key={idx}
            sx={{
              width: 283,
              height: 128,
              mb: 2,
              p: 1.5,
              bgcolor: "#F1F6FB",
              borderRadius: "12px",
              boxShadow: "0px 8px 16px rgba(15, 23, 42, 0.05)",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {/* Row 1: Login / Currency */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                bgcolor: "#FFFFFF",
                borderRadius: "8px",
                px: 1,
                py: 0.5,
              }}
            >
              {/* Login Column */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#64748B",
                  }}
                >
                  Login:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#0F172A",
                    wordBreak: "break-word",
                  }}
                >
                  {follower.login}
                </Typography>
              </Box>

              {/* Currency Column */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#64748B",
                  }}
                >
                  Currency:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#0F172A",
                  }}
                >
                  {follower.currency}
                </Typography>
              </Box>
            </Box>

            {/* Row 2: Copy Mode / Risk Ratio */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                px: 1,
              }}
            >
              {/* Copy Mode Column */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#64748B",
                  }}
                >
                  Copy Mode
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#0F172A",
                    wordBreak: "break-word",
                  }}
                >
                  {follower.copy_mode}
                </Typography>
              </Box>

              {/* Risk Ratio Column */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#64748B",
                  }}
                >
                  Risk Ratio
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#0F172A",
                  }}
                >
                  {follower.risk_ratio}
                </Typography>
              </Box>
            </Box>

            {/* Row 3: Following / Modification (with format dates) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                px: 1,
              }}
            >
              {/* Following Column */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#64748B",
                  }}
                >
                  Following
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#0F172A",
                  }}
                >
                  {formatDateShort(follower.following_date)}
                </Typography>
              </Box>

              {/* Modification Column */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#64748B",
                  }}
                >
                  Modification
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#0F172A",
                  }}
                >
                  {formatDateShort(follower.modification_date)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))
      ) : (
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: 12,
          }}
        >
          No followers found.
        </Typography>
      )}
    </Paper>
  );
};

export default FollowersPanelSidebar;